import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@asm/ui";
import {
  Building2,
  Users,
  Scan,
  Activity,
  Shield,
  CheckCircle2,
  AlertCircle,
  Server,
  Database,
  HardDrive,
  Cpu,
} from "lucide-react";
import { adminApi, SystemStats } from "../lib/api";

function StatCard({
  title,
  value,
  description,
  icon: Icon,
  loading,
  variant = "default",
}: {
  title: string;
  value: string | number;
  description: string;
  icon: React.ElementType;
  loading?: boolean;
  variant?: "default" | "red" | "emerald" | "amber";
}) {
  const variantStyles = {
    default: "border-border bg-card/50",
    red: "border-red-500/20 bg-red-500/5",
    emerald: "border-emerald-500/20 bg-emerald-500/5",
    amber: "border-amber-500/20 bg-amber-500/5",
  };

  const iconStyles = {
    default: "text-muted-foreground",
    red: "text-red-400",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
  };

  const valueStyles = {
    default: "text-foreground",
    red: "text-red-400",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
  };

  return (
    <div className={`rounded-lg border p-5 ${variantStyles[variant]} animate-fade-in-up`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
          {title}
        </span>
        <Icon className={`h-4 w-4 ${iconStyles[variant]}`} />
      </div>
      {loading ? (
        <Skeleton className="h-8 w-20" />
      ) : (
        <div className={`text-3xl font-bold font-mono ${valueStyles[variant]}`}>
          {value}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
    </div>
  );
}

function ServiceStatus({ name, icon: Icon, status }: {
  name: string;
  icon: React.ElementType;
  status: "healthy" | "degraded" | "down";
}) {
  const statusConfig = {
    healthy: {
      icon: CheckCircle2,
      className: "text-emerald-400",
      label: "Healthy",
      bg: "bg-emerald-500/10",
    },
    degraded: {
      icon: AlertCircle,
      className: "text-amber-400",
      label: "Degraded",
      bg: "bg-amber-500/10",
    },
    down: {
      icon: AlertCircle,
      className: "text-red-400",
      label: "Down",
      bg: "bg-red-500/10",
    },
  };

  const config = statusConfig[status];
  const StatusIcon = config.icon;

  return (
    <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-md ${config.bg}`}>
          <Icon className={`h-4 w-4 ${config.className}`} />
        </div>
        <span className="font-medium text-sm">{name}</span>
      </div>
      <div className="flex items-center gap-2">
        <StatusIcon className={`h-4 w-4 ${config.className}`} />
        <span className={`text-sm ${config.className}`}>{config.label}</span>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await adminApi.getStats();
        setStats(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load stats");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <div className="tactical-grid space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <Shield className="h-8 w-8 text-red-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
            <p className="text-muted-foreground">System overview and management</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="animate-fade-in-up rounded-lg border border-red-500/30 bg-red-500/5 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Organizations"
          value={stats?.total_organizations ?? 0}
          description="Active organizations"
          icon={Building2}
          loading={loading}
          variant="default"
        />
        <StatCard
          title="Users"
          value={stats?.total_users ?? 0}
          description="Total platform users"
          icon={Users}
          loading={loading}
          variant="default"
        />
        <StatCard
          title="Scans Today"
          value={stats?.scans_last_24h ?? 0}
          description="Scans run in last 24h"
          icon={Scan}
          loading={loading}
          variant="red"
        />
        <StatCard
          title="Weekly Scans"
          value={stats?.scans_last_7d ?? 0}
          description="Scans in last 7 days"
          icon={Activity}
          loading={loading}
          variant="emerald"
        />
      </div>

      {/* Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Activity */}
        <Card className="animate-fade-in-up stagger-1 border-border bg-card/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-6 w-1 rounded-full bg-red-500" />
              <div>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Latest system events</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="p-3 rounded-lg bg-muted/30 border border-border mb-4">
                <Activity className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-sm">Activity feed coming soon</p>
            </div>
          </CardContent>
        </Card>

        {/* System Health */}
        <Card className="animate-fade-in-up stagger-2 border-border bg-card/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-6 w-1 rounded-full bg-emerald-500" />
              <div>
                <CardTitle>System Health</CardTitle>
                <CardDescription>Service status</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <ServiceStatus name="API Server" icon={Server} status="healthy" />
            <ServiceStatus name="Database" icon={Database} status="healthy" />
            <ServiceStatus name="Redis Cache" icon={HardDrive} status="healthy" />
            <ServiceStatus name="Workers" icon={Cpu} status="healthy" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
